import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DualListSelectorSearchable from "components/DualListSelector/DualListSelectorSearchable";
import { DualListItem } from "components/DualListSelector";
import { TableCellType } from "components/LazyTable";
import Loader from "components/Loader";
import SnackbarMessage from "components/SnackbarMessage";
import SortableTable from "components/SortableTable";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import { DropzoneAreaBase, FileObject } from "mui-file-dropzone";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  RusticiExportOrganization,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { FileSizes } from "utils/mediaUtils";
import translateError from "utils/translateError";
import * as XLSX from "xlsx";

const CONTENT_TYPES = [
  { value: "SCORM12", labelKey: "addExportCoursesContentTypeScorm12" },
  {
    value: "SCORM20043RDEDITION",
    labelKey: "addExportCoursesContentTypeScorm2004Third",
  },
  {
    value: "SCORM20044THEDITION",
    labelKey: "addExportCoursesContentTypeScorm2004Fourth",
  },
];

const SKU_COLUMN_HEADER = "Content Provider Unique ID";

const SCORM_FILE_NAME_COLUMN_HEADER = "SCORM File Name";

const ACCEPTED_METADATA_FILES = [".xlsx", ".xlsm"];

const MAX_SKUS = 10000;

const MAX_TAG_LENGTH = 200;

const CONCURRENCY_OPTIONS = [1, 5, 10, 15, 20];
const DEFAULT_CONCURRENCY = 1;

const AddExportRusticiCourses = () => {
  const { formatMessage: t } = useIntl();
  const { rusticiCourseService } = useGlobalContext();
  const { organizationId, organizationName } = contextStore.useState();

  useTitle(t({ id: "addExportCoursesPageTitle" }));

  const [reviewing, setReviewing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<DualListItem[]>([]);
  const [skus, setSkus] = useState<string[]>([]);
  const [fileObject, setFileObject] = useState<FileObject | null>(null);
  const [contentType, setContentType] = useState(CONTENT_TYPES[0].value);
  const [exportOnly, setExportOnly] = useState(false);
  const [tag, setTag] = useState("");
  const [tagError, setTagError] = useState<string | null>(null);
  const [concurrency, setConcurrency] = useState(DEFAULT_CONCURRENCY);
  const [submitting, setSubmitting] = useState(false);
  const [parsing, setParsing] = useState(false);
  const parseToken = useRef(0);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const organizations = useQuery<RusticiExportOrganization[]>(
    ["rusticiCourseService.getExportOrganizations", organizationId],
    () => rusticiCourseService.getExportOrganizations(),
    { enabled: !!organizationId },
  );

  useEffect(() => {
    setSelected([]);
    setSearchTerm("");
    setReviewing(false);
  }, [organizationId]);

  const exportable = useMemo(
    () =>
      (organizations.data ?? []).filter(
        (organization) => organization.isAvailableInContentController !== false,
      ),
    [organizations.data],
  );

  const available = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const selectedIds = new Set(selected.map((item) => item.id));

    return exportable
      .filter((organization) => !selectedIds.has(organization.organizationId!))
      .filter(
        (organization) =>
          term.length === 0 ||
          (organization.organizationName ?? "").toLowerCase().includes(term),
      )
      .map(
        (organization) =>
          new DualListItem(
            organization.organizationId!,
            organization.organizationName!,
          ),
      );
  }, [exportable, searchTerm, selected]);

  const organizationCells: TableCellType[] = useMemo(
    () => [{ accessor: "name", label: t({ id: "nameLabel" }), type: "text" }],
    [t],
  );

  const selectedRows = useMemo(
    () => selected.map((item) => ({ id: item.id, name: item.text })),
    [selected],
  );

  const rejectFile = (messageId: string) => {
    setFileObject(null);
    setSkus([]);
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: messageId }), Severity.Error),
    );
  };

  const readMetadataFile = async (file: File) => {
    const workbook = XLSX.read(await file.arrayBuffer());

    const sheets = workbook.SheetNames.map((name) =>
      XLSX.utils.sheet_to_json<string[]>(workbook.Sheets[name], {
        header: 1,
        blankrows: false,
        raw: false,
      }),
    ).filter((rows) => rows.length > 0);

    if (
      sheets.length === 0 ||
      sheets.some((rows) => (rows[0]?.[0] ?? "").trim() !== SKU_COLUMN_HEADER)
    ) {
      rejectFile("addExportCoursesFileInvalidHeader");
      return;
    }

    const hasScormFileNameColumn = (sheets[0][0] ?? []).some(
      (cell: unknown) => String(cell ?? "").trim() === SCORM_FILE_NAME_COLUMN_HEADER,
    );

    if (!hasScormFileNameColumn) {
      rejectFile("addExportCoursesFileMissingScormColumn");
      return;
    }

    const parsed = sheets
      .flatMap((rows) => rows.slice(1))
      .map((row) => (row?.[0] ?? "").trim())
      .filter((sku) => sku.length > 0);

    if (parsed.length === 0) {
      rejectFile("addExportCoursesFileNoSkus");
      return;
    }

    if (parsed.length > MAX_SKUS) {
      rejectFile("addExportCoursesFileTooManySkus");
      return;
    }

    setSkus(Array.from(new Set(parsed)));
  };

  const canContinue = selected.length > 0 && skus.length > 0;

  const startExport = async () => {
    setSubmitting(true);

    try {
      await rusticiCourseService.startCourseExport(
        selected.map((item) => item.id),
        skus,
        contentType,
        exportOnly,
        tag.trim(),
        concurrency,
        { data: fileObject!.file, fileName: fileObject!.file.name },
      );

      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "addExportCoursesQueued" }),
          Severity.Success,
        ),
      );
      setSelected([]);
      setSkus([]);
      setTag("");
      setTagError(null);
      setFileObject(null);
      setReviewing(false);
    } catch (error: any) {
      const serverMessage = error?.data?.title ?? null;
      const duplicateTag = t({ id: "addExportCoursesTagDuplicate" });

      setSnackbarMessage(
        new SnackbarMessageProps(
          serverMessage ?? translateError(error, t),
          Severity.Error,
        ),
      );

      setTagError(serverMessage === duplicateTag ? duplicateTag : null);
      setReviewing(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (organizations.isFetching || !organizationId) {
    return <Loader />;
  }

  return (
    <Container>
      <Box my={4}>
        <Typography variant="h5" component="h1" gutterBottom>
          {t({
            id: reviewing
              ? "addExportCoursesStep2Title"
              : "addExportCoursesStep1Title",
          })}
        </Typography>

        {organizations.isError && (
          <Alert severity="error">
            {translateError(organizations.error, t)}
          </Alert>
        )}

        {!organizations.isError && exportable.length === 0 && (
          <Alert severity="warning">
            <T id="addExportCoursesNoOrganizations" />
          </Alert>
        )}

        {!reviewing && !organizations.isError && exportable.length > 0 && (
          <Paper sx={{ p: 3 }}>
            <Alert severity="info">
              <T id="addExportCoursesOrganizationsInfo" />
            </Alert>

            <Box my={2}>
              {organizationName && (
                <Typography variant="body2" fontWeight="bold">
                  <T
                    id="addExportCoursesOrganizationsUnder"
                    values={{
                      organization: (
                        <Box component="span" sx={{ color: "primary.main" }}>
                          {organizationName}
                        </Box>
                      ),
                    }}
                  />
                </Typography>
              )}
              <DualListSelectorSearchable
                key={organizationId}
                setSearchTerm={setSearchTerm}
                options={available}
                selectedOptions={selected}
                isFetching={organizations.isFetching}
                index={0}
                onChange={setSelected}
              />
            </Box>

            <Grid container spacing={3} alignItems="center" justifyContent="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="contentTypeLabel">
                    <T id="addExportCoursesContentType" />
                  </InputLabel>
                  <Select
                    labelId="contentTypeLabel"
                    label={t({ id: "addExportCoursesContentType" })}
                    value={contentType}
                    onChange={(event) =>
                      setContentType(event.target.value as string)
                    }
                  >
                    {CONTENT_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {t({ id: type.labelKey })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="concurrencyLabel">
                    <T id="addExportCoursesConcurrency" />
                  </InputLabel>
                  <Select
                    labelId="concurrencyLabel"
                    label={t({ id: "addExportCoursesConcurrency" })}
                    value={concurrency}
                    onChange={(event) =>
                      setConcurrency(Number(event.target.value))
                    }
                  >
                    {CONCURRENCY_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl>
                  <FormLabel>
                    <T id="addExportCoursesOperation" />
                  </FormLabel>
                  <RadioGroup
                    value={exportOnly ? "exportOnly" : "assignAndExport"}
                    onChange={(event) =>
                      setExportOnly(event.target.value === "exportOnly")
                    }
                  >
                    <FormControlLabel
                      value="assignAndExport"
                      control={<Radio />}
                      label={t({ id: "addExportCoursesAssignAndExport" })}
                    />
                    <FormControlLabel
                      value="exportOnly"
                      control={<Radio />}
                      label={t({ id: "addExportCoursesExportOnly" })}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  value={tag}
                  onChange={(event) => {
                    setTag(event.target.value);
                    setTagError(null);
                  }}
                  error={tagError !== null}
                  label={t({ id: "addExportCoursesTag" })}
                  helperText={
                    tagError ?? t({ id: "addExportCoursesTagAutoGenerated" })
                  }
                  inputProps={{ maxLength: MAX_TAG_LENGTH }}
                />
              </Grid>
            </Grid>

            <Box mt={3}>
              <FormLabel>
                <T id="addExportCoursesFileUpload" />
              </FormLabel>
              <Box mt={1}>
                <DropzoneAreaBase
                  acceptedFiles={ACCEPTED_METADATA_FILES}
                  maxFileSize={FileSizes.Mb10}
                  filesLimit={1}
                  dropzoneText={t({ id: "addExportCoursesFileHelp" })}
                  fileObjects={fileObject ? [fileObject] : []}
                  showFileNames
                  showAlerts={["error"]}
                  onAdd={(files: FileObject[]) => {
                    const added = files[0];

                    if (!added?.file) {
                      return;
                    }

                    const token = parseToken.current + 1;
                    parseToken.current = token;

                    setFileObject(added);
                    setSkus([]);
                    setParsing(true);

                    readMetadataFile(added.file)
                      .catch(() => {
                        if (parseToken.current === token) {
                          rejectFile("addExportCoursesFileUnreadable");
                        }
                      })
                      .finally(() => {
                        if (parseToken.current === token) {
                          setParsing(false);
                        }
                      });
                  }}
                  onDelete={() => {
                    parseToken.current += 1;
                    setParsing(false);
                    setFileObject(null);
                    setSkus([]);
                  }}
                />
              </Box>
              {parsing && (
                <Box mt={1} display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="textSecondary">
                    <T id="addExportCoursesFileReading" />
                  </Typography>
                </Box>
              )}
            </Box>

            <Box mt={3} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                disabled={!canContinue}
                onClick={() => setReviewing(true)}
              >
                <T id="addExportCoursesNext" />
              </Button>
            </Box>
          </Paper>
        )}

        {reviewing && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              <T id="addExportCoursesStepReview" />
            </Typography>

            <Box my={2}>
              <Typography variant="body2">
                <T id="addExportCoursesContentType" />:{" "}
                {t({
                  id: CONTENT_TYPES.find((type) => type.value === contentType)!
                    .labelKey,
                })}
              </Typography>
              <Typography variant="body2">
                <T id="addExportCoursesOperation" />:{" "}
                {t({
                  id: exportOnly
                    ? "addExportCoursesExportOnly"
                    : "addExportCoursesAssignAndExport",
                })}
              </Typography>
              <Typography variant="body2">
                <T id="addExportCoursesConcurrency" />: {concurrency}
              </Typography>
              <Typography variant="body2">
                <T id="addExportCoursesTag" />:{" "}
                {tag.trim() || t({ id: "addExportCoursesTagAutoGeneratedValue" })}
              </Typography>
              <Typography variant="body2">
                <T id="addExportCoursesFile" />: {fileObject?.file.name} (
                {skus.length} {t({ id: "addExportCoursesSkusLoaded" })})
              </Typography>
            </Box>

            <Typography variant="body2" gutterBottom>
              <T id="addExportCoursesOrganizations" /> ({selected.length})
            </Typography>
            <SortableTable
              {...{
                rows: selectedRows,
                cells: organizationCells,
                dense: true,
              }}
            />

            <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                disabled={submitting}
                onClick={() => setReviewing(false)}
              >
                <T id="backLabel" />
              </Button>
              <Button
                variant="contained"
                disabled={submitting}
                onClick={startExport}
              >
                <T id="addExportCoursesStart" />
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      <Dialog open={submitting} disableEscapeKeyDown maxWidth="xs" fullWidth>
        <DialogContent>
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            py={2}
            px={1}
          >
            <CircularProgress size={28} />
            <Box>
              <Typography variant="subtitle1">
                <T id="addExportCoursesPreparing" />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <T id="addExportCoursesPreparingDetail" />
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default AddExportRusticiCourses;
